/**
 * Panneau Filtrer, toutes largeurs : groupe replié « Plus de critères ».
 *
 * Les facettes secondaires portent `data-facet-more` et suivent l'en-tête du groupe dans le HTML ; le
 * CSS les cache tant que le contenu du panneau n'a pas la classe `--more-open`. L'état ouvert est gardé
 * pour la session ; le groupe s'ouvre aussi de lui-même dès qu'un de ses filtres devient actif.
 */
import { rangeFiltersState } from "@/services/algoliaRouting.blade";

const STORAGE_KEY = "marketplace:filters-more-open";
const OPEN_CLASS = "marketplace-sidebar__content--more-open";

/**
 * Les 9 rubriques repliées sous « Plus de critères » (`data-facet-more` de sidebar-widget-panels) :
 * Carrosserie, Options, Matériau intérieur, Places, Couleur intérieure (listes), Année, Puissance,
 * Poids, CO2 (plages). Finition et Couleur extérieure restent hors du groupe : jamais comptées.
 */
export const MORE_REFINEMENT_ATTRIBUTES = [
    "body_type",
    "options",
    "reference_interior_material",
    "seats",
    "reference_interior_color",
] as const;
/** Rubriques du groupe adossées aux plages (`rangeFiltersState`). */
export const MORE_RANGE_ATTRIBUTES = ["registration_at", "power", "weight", "co2_emission"] as const;

type RefinementsState = {
    disjunctiveFacetsRefinements?: Record<string, unknown[] | undefined>;
    facetsRefinements?: Record<string, unknown[] | undefined>;
};

let group: { content: HTMLElement; button: HTMLButtonElement; active: HTMLElement | null } | null =
    null;
let lastActiveCount = 0;

function readStoredOpen(): boolean {
    try {
        return window.sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

function storeOpen(isOpen: boolean): void {
    try {
        if (isOpen) {
            window.sessionStorage.setItem(STORAGE_KEY, "1");
        } else {
            window.sessionStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // Stockage indisponible (navigation privée, cookies bloqués) : l'état reste en mémoire.
    }
}

function setGroupOpen(isOpen: boolean): void {
    if (!group) return;
    const { content, button } = group;
    content.classList.toggle(OPEN_CLASS, isOpen);
    button.setAttribute("aria-expanded", String(isOpen));
    const minus = button.querySelector<HTMLElement>(".widget-header__icon--minus");
    const plus = button.querySelector<HTMLElement>(".widget-header__icon--plus");
    if (minus) minus.hidden = !isOpen;
    if (plus) plus.hidden = isOpen;
}

/** Facettes du groupe affichées, dans l'ordre du HTML (celui de l'écran). */
function visibleMorePanels(content: HTMLElement): HTMLElement[] {
    return [...content.querySelectorAll<HTMLElement>("[data-facet-more]")].filter(
        (panel) => panel.offsetParent !== null,
    );
}

/** Vrai quand la facette est cachée dans le groupe replié. */
export function isFacetHiddenInMoreGroup(panel: Element | null): boolean {
    if (!panel || !panel.hasAttribute("data-facet-more")) return false;
    const content = panel.closest(".marketplace-sidebar__content");
    return !!content && !content.classList.contains(OPEN_CLASS);
}

/** Nombre de facettes du groupe qui ont un filtre actif. */
export function countActiveMoreFacets(state: RefinementsState | null | undefined): number {
    const refinements = (attribute: string): number =>
        (state?.disjunctiveFacetsRefinements?.[attribute]?.length ?? 0) +
        (state?.facetsRefinements?.[attribute]?.length ?? 0);
    const ranges = rangeFiltersState.value;
    let count = 0;
    for (const attribute of MORE_REFINEMENT_ATTRIBUTES) {
        if (refinements(attribute) > 0) count += 1;
    }
    for (const attribute of MORE_RANGE_ATTRIBUTES) {
        const range = ranges[attribute];
        if (range?.min !== undefined || range?.max !== undefined) count += 1;
    }
    return count;
}

/**
 * « Plus de critères (9) · 1 actif » dans l'en-tête ; ouvre le groupe quand un filtre du groupe
 * s'ajoute (au chargement, ou ensuite), sans le rouvrir de force si l'utilisateur l'a refermé.
 */
export function updateMoreFacetsActiveCount(count: number): void {
    if (!group) return;
    if (group.active) {
        group.active.hidden = count === 0;
        group.active.textContent = count === 0 ? "" : ` · ${count} actif${count > 1 ? "s" : ""}`;
    }
    if (count > lastActiveCount && !group.content.classList.contains(OPEN_CLASS)) {
        setGroupOpen(true);
    }
    lastActiveCount = count;
}

export function initSidebarMoreFacets(root: ParentNode = document): void {
    const toggle = root.querySelector<HTMLElement>("[data-facet-more-toggle]");
    const content = toggle?.closest<HTMLElement>(".marketplace-sidebar__content");
    const button = toggle?.querySelector<HTMLButtonElement>("button");
    if (!toggle || !content || !button) return;

    group = { content, button, active: toggle.querySelector<HTMLElement>("[data-facet-more-active]") };

    const ids: string[] = [];
    content.querySelectorAll<HTMLElement>("[data-facet-more]").forEach((panel) => {
        if (!panel.id) panel.id = `sidebar-facet-${panel.dataset.widget ?? ids.length}`;
        ids.push(panel.id);
    });
    if (ids.length) button.setAttribute("aria-controls", ids.join(" "));

    setGroupOpen(readStoredOpen());

    button.addEventListener("click", (event) => {
        const isOpen = !content.classList.contains(OPEN_CLASS);
        setGroupOpen(isOpen);
        storeOpen(isOpen);
        // Ouverture au clavier (Entrée/Espace : `detail` à 0) : le focus va sur la première facette
        // du groupe.
        if (isOpen && event.detail === 0) {
            visibleMorePanels(content)[0]
                ?.querySelector<HTMLElement>(".widget-header")
                ?.focus();
        }
    });
}
